import React from 'react';
import { IPendingWrapUp, isCrmError, useCrmScheduling } from '../../../services/crm';

const POLL_MS = 60_000;

// #region useMeetingWrapUps
/**
 * The rep's wrap-up queue, kept fresh while they work: fetched on mount, when
 * the tab regains focus, and once a minute. The server is the source of truth,
 * so a wrap-up handled on the phone disappears from the laptop on the next tick.
 */
export const useMeetingWrapUps = (enabled: boolean) => {
    const [scheduling] = useCrmScheduling();
    const apiRef = React.useRef(scheduling);
    apiRef.current = scheduling;
    const [queue, setQueue] = React.useState<IPendingWrapUp[]>([]);
    const [loaded, setLoaded] = React.useState<boolean>(false);

    const refresh = React.useCallback(async () => {
        if (!enabled) return;
        const r = await apiRef.current.pendingWrapUps();
        if (!isCrmError(r)) { setQueue(r); setLoaded(true); }
    }, [enabled]);

    React.useEffect(() => {
        if (!enabled) return;
        refresh();
        const t = setInterval(refresh, POLL_MS);
        const onFocus = () => { if (document.visibilityState === 'visible') refresh(); };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onFocus);
        return () => { clearInterval(t); window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onFocus); };
    }, [enabled, refresh]);

    /** Drop one card right away (after wrap-up, snooze or skip) without waiting for the next poll. */
    const remove = React.useCallback((botId: number) => setQueue((q) => q.filter((w) => w.bot.id !== botId)), []);

    return { queue, loaded, refresh, remove };
};
